/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormGroup,
  Box,
  Grid,
} from '@mui/material';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import TextFieldSelectBox from '../../component/SelectBox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {patchDailySchedule} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {pullDownMenu} from '../../define';
import DialogPage from '../../component/DialogMessage';

/**
 * グループ内の別の特定日へスケジュールをコピーするダイアログ
 * @param props props
 * - isOpenDialog ダイアログ表示フラグ
 * - setIsOpenDialog ダイアログ表示フラグ 設定関数
 * - scheduleChangeHandler 特定日スケジュール更新関数
 * - targetGroupID グループID
 * - targetGroupName グループ名
 * - dailyScheduleData 特定日のレスポンスデータ
 * @returns JSX
 */
const CopyToTemplateDialog = (props: {
  isOpenDialog: boolean;
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  scheduleChangeHandler: () => void;
  targetGroupID: string;
  targetGroupName: string;
  dailyScheduleData: apiIF.res.getDailySchedule;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  //セレクトボックスで選択したコピー元の特定日タイプ
  const [selectTemplate, setSelectTemplate] =
    useState<keyof apiIF.res.getDailySchedule>('holiday-schedule');
  //チェックボックスで選択したコピー先の特定日タイプ
  const [checkTemplate, setCheckTemplate] = useState<
    (keyof apiIF.res.getDailySchedule)[]
  >([]);

  //フラグ
  /** 確認ダイアログのオープンフラグ */
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示フラグ

  //特定日タイプ項目
  const tempType: {
    name: keyof apiIF.res.getDailySchedule;
    label: string;
  }[] = [
    {
      name: 'weekday-schedule',
      label: trans('CopyToTemplateDialog.checkBoxLabel.weekday'),
    },
    {
      name: 'holiday-schedule',
      label: trans('CopyToTemplateDialog.checkBoxLabel.holiday'),
    },
    {
      name: 'special-day-1-schedule',
      label: trans('CopyToTemplateDialog.checkBoxLabel.specialDay1'),
    },
    {
      name: 'special-day-2-schedule',
      label: trans('CopyToTemplateDialog.checkBoxLabel.specialDay2'),
    },
  ];

  /**
   * チェックボックスの特定日変更時処理
   * @param event
   */
  const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetName = event.target.name as keyof apiIF.res.getDailySchedule;
    if (checkTemplate.includes(targetName)) {
      setCheckTemplate(checkTemplate.filter(item => item !== targetName));
    } else {
      setCheckTemplate([...checkTemplate, targetName]);
    }
  };

  /**
   * COPYボタン押下時処理
   */
  const handleClickCopy = async () => {
    setAPIWait(true);
    if (siteID === undefined) return;
    //特定日スケジュールデータの初期値を設定
    let copyData: apiIF.reqParameters.patchDailySchedule = {};
    //prettier-ignore
    //特定日４つをループ
    for (const key of Object.keys(props.dailyScheduleData) as (keyof apiIF.reqParameters.patchDailySchedule)[]) {
      //チェックボックスで選択されていない特定日のスケジュールは削除
      if (checkTemplate.includes(key) === false) {
        continue;
      }
      copyData[key] = {schedule: props.dailyScheduleData[selectTemplate]};
    }
    //特定日スケジュールを編集
    try {
      await patchDailySchedule(siteID, props.targetGroupID, copyData);
      //スケジュール設定更新
      props.scheduleChangeHandler();
      handleClose();
    } catch (error) {
      apiErrorAlert(trans);
    } finally {
      setAPIWait(false);
      setIsOpenDialog(false);
    }
  };

  /**
   * ダイアログを閉じる処理
   */
  const handleClose = () => {
    setCheckTemplate([]);
    setSelectTemplate('holiday-schedule');
    props.setIsOpenDialog(false);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('CopyToTemplateDialog.title')}
        </DialogTitle>
        <DialogContent className="schedule__copyDialog__content">
          <Stack spacing={2}>
            {/** グループの名前 */}
            <Box>{props.targetGroupName}</Box>
            {/** コピー元の特定日の種類選択欄 */}
            <Box className="schedule__copyDialog__content__specificScheduleSelectArea">
              <TextFieldSelectBox
                label={trans('CopyToTemplateDialog.selectBoxLabel')}
                choices={pullDownMenu.SCHEDULE_TEMPLATE}
                value={selectTemplate}
                setValue={setSelectTemplate}
                class="schedule__copyDialog__content__specificScheduleSelectArea__selectBox"
              />
            </Box>
            {/** コピー先の特定日の種類選択欄 */}
            <Box className="schedule__copyDialog__content__specificScheduleCheckArea">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={2}>
                  {trans('CopyToTemplateDialog.to')}
                </Grid>
                <Grid item xs={10}>
                  <FormControl>
                    <FormGroup>
                      {tempType.map(info => (
                        <FormControlLabel
                          key={`selectType-${info.name}`}
                          control={
                            <Checkbox
                              disabled={selectTemplate === info.name}
                              checked={checkTemplate.includes(info.name)}
                              onChange={handleChangeType}
                              name={info.name}
                            />
                          }
                          label={info.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('CopyToTemplateDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={() => setIsOpenDialog(true)}
            apiWait={apiWait}
            disabled={checkTemplate.length === 0}
            buttonText={trans('CopyToTemplateDialog.buttonTxt.copy')}
          />
        </DialogActions>
      </Dialog>
      <DialogPage
        open={isOpenDialog}
        setOpen={setIsOpenDialog}
        titleString={trans('Confirm.dialogBox.title')}
        contentText={trans('Confirm.dialogBox.msg')}
        buttonNameLeft={trans('Confirm.dialogBox.buttonTxt.cancel')}
        buttonNameRight={trans('Confirm.dialogBox.buttonTxt.ok')}
        redirectPath={''}
        execFunc={handleClickCopy}
        id="mv-CopyOtherGroupDialog"
      />
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};
export default CopyToTemplateDialog;
